<template>
  <UDashboardPanel id="dead-letters" :ui="{ body: 'h-full w-full sm:p-0 p-0' }">
    <template #body>
      <div class="w-full h-full flex flex-col">
        <div class="px-4 pt-4 pb-4">
          <div class="flex flex-wrap items-center gap-2">
            <UInput
              v-model="filterMessageType"
              class="w-full sm:w-56"
              icon="i-lucide-message-square"
              :placeholder="t('deadLetters.filters.messageType')"
              size="sm"
            />
            <UInput
              v-model="filterExceptionType"
              class="w-full sm:w-56"
              icon="i-lucide-alert-triangle"
              :placeholder="t('deadLetters.filters.exceptionType')"
              size="sm"
            />
            <UInput
              v-model="filterExceptionMessage"
              class="w-full sm:w-56"
              icon="i-lucide-search"
              :placeholder="t('deadLetters.filters.exceptionMessage')"
              size="sm"
            />
            <UInput
              v-model="filterFrom"
              type="datetime-local"
              class="w-full sm:w-48"
              size="sm"
            />
            <UInput
              v-model="filterUntil"
              type="datetime-local"
              class="w-full sm:w-48"
              size="sm"
            />
            <UButton
              v-if="hasActiveFilters"
              icon="i-lucide-x"
              size="sm"
              variant="ghost"
              color="neutral"
              :label="t('deadLetters.filters.clear')"
              @click="clearFilters"
            />
          </div>
        </div>
        
        <div v-if="selectedIds.size > 0" class="px-4 py-2 bg-primary-50 dark:bg-primary-900/20 border-b border-(--ui-border) flex items-center gap-3">
          <span class="text-sm font-medium">{{ selectedIds.size }} {{ t('deadLetters.actions.selected') }}</span>
          <UButton icon="i-lucide-rotate-ccw" size="sm" color="primary" :label="t('deadLetters.actions.replay')" @click="handleReplay" />
          <UButton icon="i-lucide-trash" size="sm" color="error" variant="ghost" :label="t('deadLetters.actions.delete')" @click="showDeleteConfirm = true" />
          <UButton size="sm" variant="ghost" color="neutral" :label="t('deadLetters.actions.clearSelection')" @click="clearSelection" />
        </div>
        
        <div v-if="status === 'success' && data.length === 0" class="flex flex-col items-center justify-center py-20 text-center">
          <UIcon name="i-lucide-inbox" class="text-5xl text-(--ui-text-dimmed) mb-4" />
          <h3 class="text-lg font-semibold text-(--ui-text)">{{ t('deadLetters.empty.title') }}</h3>
          <p class="text-sm text-(--ui-text-muted) mt-1">{{ t('deadLetters.empty.description') }}</p>
        </div>
        
        <div v-else class="flex-1 flex flex-col overflow-hidden">
          <div class="flex-1 min-h-0 pb-[115px] lg:pb-0">
            <UTable
              :columns="columns"
              :data="tableData"
              sticky
              @hover="() => {}"
              class="w-full h-full px-4"
              :ui="{
                base: 'table-fixed border-separate border-spacing-0',
                thead: '[&>tr]:bg-(--ui-bg-elevated)/50 [&>tr]:after:content-none',
                tbody: '[&>tr]:last:[&>td]:border-b-0',
                th: 'py-2 first:rounded-l-lg last:rounded-r-lg border-y border-(--ui-border) first:border-l last:border-r',
                td: 'border-b border-(--ui-border)'
              }"
            />
          </div>
          
          <div class="fixed bottom-0 left-0 right-0 lg:sticky bg-(--ui-bg) border-t border-(--ui-border) px-4 py-3.5 z-10">
            <div class="flex items-center justify-between gap-3 min-h-8">
              <div class="text-sm text-(--ui-text-muted)">
                <span v-if="pagination">
                  <span class="hidden lg:inline">{{ t('deadLetters.table.showing') }}</span> {{ ((pagination.currentPage - 1) * pagination.pageSize) + 1 }} - {{ Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems) }} <span class="hidden lg:inline">{{ t('deadLetters.table.of') }}</span><span class="lg:hidden">/</span> {{ pagination.totalItems }}
                </span>
                <span v-else>{{ t('deadLetters.table.loading') }}</span>
              </div>
              
              <div class="flex items-center gap-3">
                <USelect
                  v-model="selectedPageSize"
                  :items="pageSizeOptions"
                  size="sm"
                  class="w-20 cursor-pointer"
                  @update:model-value="handlePageSizeChange"
                />
                <UPagination
                  v-if="pagination && pagination.totalItems > pagination.pageSize"
                  v-model:page="currentPaginationPage"
                  :total="pagination.totalItems"
                  :items-per-page="pagination.pageSize"
                  :sibling-count="1"
                  size="sm"
                  class="lg:hidden"
                  :ui="{
                    item: 'cursor-pointer',
                    prev: 'cursor-pointer',
                    next: 'cursor-pointer',
                    first: 'hidden',
                    last: 'hidden'
                  }"
                />
                <UPagination
                  v-if="pagination && pagination.totalItems > pagination.pageSize"
                  v-model:page="currentPaginationPage"
                  :total="pagination.totalItems"
                  :items-per-page="pagination.pageSize"
                  :sibling-count="2"
                  size="sm"
                  class="hidden lg:flex"
                  :ui="{
                    item: 'cursor-pointer',
                    prev: 'cursor-pointer',
                    next: 'cursor-pointer',
                    first: 'cursor-pointer',
                    last: 'cursor-pointer'
                  }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </UDashboardPanel>
  
  <ConfirmationModal
    v-model="showDeleteConfirm"
    :title="t('deadLetters.confirm.deleteTitle')"
    :message="t('deadLetters.confirm.deleteMessage', { count: selectedIds.size })"
    :confirm-text="t('deadLetters.actions.delete')"
    confirm-color="error"
    @confirm="handleDelete"
  />
  
  <ConfirmationModal
    v-model="showDeleteSingleConfirm"
    :title="t('deadLetters.confirm.deleteTitle')"
    :message="t('deadLetters.confirm.deleteSingleMessage')"
    :confirm-text="t('deadLetters.actions.delete')"
    confirm-color="error"
    @confirm="confirmDeleteSingle"
  />
</template>

<script setup lang="ts">
import { computed, h, onMounted, ref, resolveComponent, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePreferences } from '@/store'
import { useDeadLettersData, type DeadLetterFilters } from '@/composables/useDeadLettersData'
import { formatDate } from '@/utils/date'
import { debounce, showSuccess, showError } from '@/utils/utils'
import ConfirmationModal from '@/components/ConfirmationModal.vue'

const UButton = resolveComponent('UButton')
const UBadge = resolveComponent('UBadge')
const UTooltip = resolveComponent('UTooltip')
const UCheckbox = resolveComponent('UCheckbox')

const { t, locale } = useI18n()
const preferences = usePreferences()

const { data, status, pagination, fetchDeadLetters, applyFilters, goToPage, changePageSize, replay, remove } = useDeadLettersData()

const selectedIds = ref(new Set<string>())
const showDeleteConfirm = ref(false)
const showDeleteSingleConfirm = ref(false)
const deleteSingleId = ref<string | null>(null)

const filterMessageType = ref('')
const filterExceptionType = ref('')
const filterExceptionMessage = ref('')
const filterFrom = ref('')
const filterUntil = ref('')

const selectedPageSize = ref(preferences.deadLettersPageSize)
const currentPaginationPage = ref(1)

const pageSizeOptions = [
  { label: '20', value: 20 },
  { label: '40', value: 40 },
  { label: '50', value: 50 },
  { label: '100', value: 100 }
]

const hasActiveFilters = computed(() =>
  !!filterMessageType.value || !!filterExceptionType.value || !!filterExceptionMessage.value || !!filterFrom.value || !!filterUntil.value
)

const buildFilters = (): DeadLetterFilters => ({
  messageType: filterMessageType.value || undefined,
  exceptionType: filterExceptionType.value || undefined,
  exceptionMessage: filterExceptionMessage.value || undefined,
  from: filterFrom.value ? new Date(filterFrom.value).toISOString() : undefined,
  until: filterUntil.value ? new Date(filterUntil.value).toISOString() : undefined,
})

const debouncedApplyFilters = debounce(async () => {
  await applyFilters(buildFilters())
}, 400)

watch([filterMessageType, filterExceptionType, filterExceptionMessage, filterFrom, filterUntil], () => {
  debouncedApplyFilters()
})

const clearFilters = () => {
  filterMessageType.value = ''
  filterExceptionType.value = ''
  filterExceptionMessage.value = ''
  filterFrom.value = ''
  filterUntil.value = ''
}

const allSelected = computed(() =>
  data.value.length > 0 && data.value.every(e => selectedIds.value.has(e.id))
)

const toggleSelectAll = () => {
  if (allSelected.value) {
    selectedIds.value = new Set()
  } else {
    selectedIds.value = new Set(data.value.map(e => e.id))
  }
}

const toggleRow = (id: string) => {
  const next = new Set(selectedIds.value)
  
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  
  selectedIds.value = next
}

const clearSelection = () => {
  selectedIds.value = new Set()
}

const shortTypeName = (fullType: string) => {
  if (!fullType) return ''
  
  const parts = fullType.split('.')
  
  return parts[parts.length - 1]
}

const skeletonData = computed(() => {
  if (status.value !== 'pending') return []
  
  return Array.from({ length: 5 }, (_, index) => ({
    id: `skeleton-${index}`,
    messageType: '',
    exceptionType: '',
    exceptionMessage: '',
    sentAt: '',
    receivedAt: '',
    replayable: false,
    _skeleton: true
  }))
})

const tableData = computed(() =>
  status.value === 'pending' ? skeletonData.value : data.value
)

const columns = computed(() => [
  {
    id: 'select',
    header: () => h(UCheckbox, {
      modelValue: allSelected.value,
      'onUpdate:modelValue': () => toggleSelectAll(),
    }),
    cell: ({ row }) => {
      if (row.original._skeleton) {
        return h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-4' })
      }
      
      return h(UCheckbox, {
        modelValue: selectedIds.value.has(row.original.id),
        'onUpdate:modelValue': () => toggleRow(row.original.id),
      })
    },
    size: 40
  },
  {
    id: 'messageType',
    header: t('deadLetters.table.messageType'),
    cell: ({ row }) => {
      if (row.original._skeleton) {
        return h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-40' })
      }
      
      const short = shortTypeName(row.original.messageType)
      
      return h(UTooltip, { text: row.original.messageType }, {
        default: () => h('span', { class: 'text-sm text-(--ui-text) truncate block max-w-[200px]' }, short)
      })
    },
    size: 220
  },
  {
    id: 'exceptionType',
    header: t('deadLetters.table.exceptionType'),
    cell: ({ row }) => {
      if (row.original._skeleton) {
        return h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-32' })
      }
      
      const short = shortTypeName(row.original.exceptionType)
      
      return h(UBadge, { color: 'error', variant: 'subtle', class: 'truncate max-w-[180px]' }, () => short)
    },
    size: 200
  },
  {
    id: 'exceptionMessage',
    header: t('deadLetters.table.exceptionMessage'),
    cell: ({ row }) => {
      if (row.original._skeleton) {
        return h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-48' })
      }
      
      return h(UTooltip, { text: row.original.exceptionMessage }, {
        default: () => h('span', { class: 'text-sm text-(--ui-text-muted) truncate block max-w-[250px]' }, row.original.exceptionMessage)
      })
    },
    size: 270
  },
  {
    id: 'sentAt',
    header: t('deadLetters.table.sentAt'),
    cell: ({ row }) => {
      if (row.original._skeleton) {
        return h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-28' })
      }
      
      return h('span', { class: 'text-sm text-(--ui-text-muted)' },
        formatDate(row.original.sentAt, 'datetime', locale.value)
      )
    },
    size: 160
  },
  {
    id: 'replayable',
    header: t('deadLetters.table.replayable'),
    cell: ({ row }) => {
      if (row.original._skeleton) {
        return h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-12' })
      }
      
      return h(UBadge, {
        color: row.original.replayable ? 'success' : 'neutral',
        variant: 'subtle'
      }, () => row.original.replayable ? t('deadLetters.badge.yes') : t('deadLetters.badge.no'))
    },
    size: 100
  },
  {
    id: 'actions',
    header: t('deadLetters.table.actions'),
    cell: ({ row }) => {
      if (row.original._skeleton) {
        return h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-7 w-16 rounded' })
      }
      
      const buttons = []
      
      if (row.original.replayable) {
        buttons.push(h(UButton, {
          icon: 'i-lucide-rotate-ccw',
          variant: 'ghost',
          size: 'sm',
          title: t('deadLetters.actions.replayOne'),
          class: 'cursor-pointer',
          onClick: () => replaySingle(row.original.id)
        }))
      }
      
      buttons.push(h(UButton, {
        icon: 'i-lucide-trash',
        variant: 'ghost',
        color: 'error',
        size: 'sm',
        title: t('deadLetters.actions.deleteOne'),
        class: 'cursor-pointer',
        onClick: () => deleteSingle(row.original.id)
      }))
      return h('div', { class: 'flex items-center gap-1' }, buttons)
    },
    size: 100
  }
])

const replaySingle = async (id: string) => {
  const ok = await replay([id])
  
  if (ok) {
    showSuccess(t('deadLetters.notification.replayed'))
    
    await fetchDeadLetters(pageSize(), apiPage(), buildFilters(), true)
  } else {
    showError(t('deadLetters.notification.replayFailed'))
  }
}

const deleteSingle = (id: string) => {
  deleteSingleId.value = id
  showDeleteSingleConfirm.value = true
}

const confirmDeleteSingle = async () => {
  if (!deleteSingleId.value) return
  
  const ok = await remove([deleteSingleId.value])
  
  if (ok) {
    showSuccess(t('deadLetters.notification.deleted'))
    
    await fetchDeadLetters(pageSize(), apiPage(), buildFilters(), true)
  } else {
    showError(t('deadLetters.notification.deleteFailed'))
  }
  
  deleteSingleId.value = null
}

const handleDelete = async () => {
  const ok = await remove(Array.from(selectedIds.value))
  
  if (ok) {
    showSuccess(t('deadLetters.notification.deleted'))
    clearSelection()
    await fetchDeadLetters(pageSize(), apiPage(), buildFilters(), true)
  } else {
    showError(t('deadLetters.notification.deleteFailed'))
  }
}

const handleReplay = async () => {
  const ok = await replay(Array.from(selectedIds.value))
  
  if (ok) {
    showSuccess(t('deadLetters.notification.replayed'))
    clearSelection()
    await fetchDeadLetters(pageSize(), apiPage(), buildFilters(), true)
  } else {
    showError(t('deadLetters.notification.replayFailed'))
  }
}

const pageSize = () => selectedPageSize.value
const apiPage = () => (currentPaginationPage.value ?? 1) - 1

const handlePageSizeChange = async (newSize: number) => {
  await changePageSize(newSize)
  
  selectedPageSize.value = newSize
  currentPaginationPage.value = 1
  preferences.setDeadLettersPageSize(newSize)
}

watch(pagination, newPagination => {
  if (!newPagination) return
  
  if (typeof newPagination.pageSize === 'number') {
    selectedPageSize.value = newPagination.pageSize
  }
  
  if (typeof newPagination.currentPage === 'number') {
    currentPaginationPage.value = newPagination.currentPage
  }
}, { immediate: true })

watch(currentPaginationPage, async (newPage, oldPage) => {
  if (newPage === oldPage) return
  
  if (newPage !== pagination.value?.currentPage) {
    await goToPage(newPage)
  }
})

onMounted(async () => {
  await fetchDeadLetters(selectedPageSize.value, 0, buildFilters())
})
</script>
